import React, { ReactNode } from 'react';
import { QUOTE_STEPS, COMPONENTS, APP_CONFIG } from "../AppConstants";
import { IAppState } from "../state/interfaces";
import { IVehicle, emptyVehicle, IQuoteApiClient, IPaths } from "../classes/Domain";
import Utilities from "../classes/Utilities";
import QuoteStep from "./QuoteStepLayout";
import { IQuoteStepState, IVehicleStepProps } from "../components/interfaces";
import QuoteStepBase from '../components/QuoteStepBase'
import { connect } from "react-redux";
import Select, { ValueType, ActionMeta, components } from 'react-select'
import { Button } from 'react-bootstrap'
import ReactLoading from 'react-loading';
import VehicleExplorer from "../classes/VehicleExplorer";
import * as _ from 'lodash';
import QuoteActions from '../state/appActions';
import ValidatableFormGroup from '../components/ValidatableFormGroup';


const VEHICLE_OPTION_TYPES = {
    YEAR: "YEAR",
    MAKE: "MAKE",
    MODEL: "MODEL",
    BODY: "BODY",
    ENGINE: "ENGINE",
    CYLINDERS: "CYLINDERS",
    SPECS: "SPECS",
    FINISH: "FINISH"
};

const NO_OPTIONS = "No_options";

const START_YEAR = 1980;

type VehicleOption = {
    value: string, 
    label: string,
    type: string,
    icon?: string
}

type OptionsGroup = {
    label: string,
    options: Array<VehicleOption>;
}

class VinRecord  {
    year: number;
    make: string;
    model: string;
    body: string;
    engineSize: string;
    specsCombined: string;
    vin: string;
    cylinders: number;
    antiThft: string;
    compSymbol: string;
    collSymbol: string;
    costNewId: string;

    constructor() {
        this.year = 0;
        this.cylinders = 0;
        this.make = "";
        this.model = "";
        this.body = "";
        this.engineSize = "";
        this.specsCombined = "";
        this.vin = "";
        this.antiThft = "";
        this.compSymbol = "";
        this.collSymbol = "";
        this.costNewId = "";
    }
}

interface IVehicleState extends IQuoteStepState {
    selectedOptions: Array<VehicleOption>,
    menuIsOpen: boolean,
    loadingOptions: boolean,
    saveAndContinue: boolean,
    updatingAppState: boolean,
    options: Array<OptionsGroup>,
    vehicle: IVehicle,
    vinValid: boolean;
}

export interface IVehicleOptionsResponse {
    options: Array<VehicleOption>
}

interface VehicleYMMProps extends IVehicleStepProps {
    nextPageUrl?: string,
    header?: ReactNode | null,
    zipCode : string,
    createQuote: (zipCode: string, vehicle: IVehicle) => Promise<any>,
    quoteApiClient: IQuoteApiClient,
    PATHS: IPaths;
    //updateAppState: (update: any) => Promise<any>;
}

const Option = props => {
    return (
        <components.Option {...props}>
            { props.data.icon
                ? (<div className="text-uppercase"><img src={props.data.icon} className="make-icon" /><span>{props.data.label}</span></div>)
                : <div className="text-uppercase">{props.data.label}</div>
            }
        </components.Option>
    );
};

class VehicleYMM extends QuoteStepBase<VehicleYMMProps, IVehicleState> {

    vehicleExplorer: VehicleExplorer;
    vinRecords: Array<VinRecord>;
    apiClient: IQuoteApiClient;
    selectRef: React.RefObject<Select>;

    constructor(props: VehicleYMMProps) {
        super(props);
        this.selectRef = React.createRef()
        this.apiClient = this.props.quoteApiClient;
        this.vehicleExplorer = new VehicleExplorer();
        this.vinRecords = [];

        this.saveAndContinue = this.saveAndContinue.bind(this);
        this.yearStepHandler = this.yearStepHandler.bind(this);
        this.makeStepHandler = this.makeStepHandler.bind(this);
        this.modelStepHandler = this.modelStepHandler.bind(this);
        this.bodyStepHandler = this.bodyStepHandler.bind(this);
        this.engineStepHandler = this.engineStepHandler.bind(this);
        this.cylindersStepHandler = this.cylindersStepHandler.bind(this);
        this.specsStepHandler = this.specsStepHandler.bind(this);
        this.finishStepHandler = this.finishStepHandler.bind(this);
        this.vinStepHandler = this.vinStepHandler.bind(this);

        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.YEAR, this.yearStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.MAKE, this.makeStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.MODEL, this.modelStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.BODY, this.bodyStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.ENGINE, this.engineStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.CYLINDERS, this.cylindersStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.SPECS, this.specsStepHandler);
        this.vehicleExplorer.addVehicleExploreStep(VEHICLE_OPTION_TYPES.FINISH, this.finishStepHandler);

        this.state = {
            selectedOptions : [],
            menuIsOpen : true,
            loadingOptions : false,
            saveAndContinue : false,
            updatingAppState : false,
            options: [],
            vehicle: emptyVehicle,
            vinValid: true
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();

        if (this.state.selectedOptions.length) {
            const lastSelectedOption = this.state.selectedOptions[this.state.selectedOptions.length - 1];
            
            const nextStep = this.vehicleExplorer.findNextStepFor(lastSelectedOption.type);
            if (nextStep) {
                nextStep.handler(lastSelectedOption.value);
            }
        } else {
            const nextStep = this.vehicleExplorer.findStepByType(VEHICLE_OPTION_TYPES.YEAR);
            if (nextStep) {
                nextStep.handler();
            }
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            nextProps.meta.propsSource = "";
            const vehicle = nextProps.vehicle;
            
            if (vehicle) {
                const selectedOptions = [
                    { value: vehicle.year.toString(), label: vehicle.year.toString(), type: VEHICLE_OPTION_TYPES.YEAR },
                    { value: vehicle.make, label: vehicle.make, type: VEHICLE_OPTION_TYPES.MAKE },
                    { value: vehicle.model, label: vehicle.model, type: VEHICLE_OPTION_TYPES.MODEL },
                    { value: vehicle.body, label: vehicle.body, type: VEHICLE_OPTION_TYPES.BODY },
                    { value: vehicle.engineSize, label: vehicle.engineSize, type: VEHICLE_OPTION_TYPES.ENGINE },
                    { value: vehicle.cylinders.toString(), label: vehicle.cylinders.toString(), type: VEHICLE_OPTION_TYPES.CYLINDERS }
                ];

                //if (vehicle.specs) {
                //    selectedOptions.push({ value: vehicle.specs, label: vehicle.specs, type: VEHICLE_OPTION_TYPES.SPECS });
                //}

                const nextState = {
                    ...prevState,
                    vehicle: vehicle,
                    selectedOptions: selectedOptions
                };

                return nextState;
            }
        }

        return prevState;
    }

    getVinRecords(year: number, make: string, model: string, vin: string | null) {
        if (0 === this.vinRecords.length) {
            return this.apiClient
                .getVinRecords(year, make, model, this.props.quoteUid, vin)
                .then((response) => {
                    if (response.data.options && 0 < response.data.options.length) {
                        this.vinRecords = _.map(response.data.options,
                            (o) => {
                                var r = new VinRecord();
                                r.year = o.year;
                                r.make = o.make;
                                r.model = o.model;
                                r.body = o.body;
                                r.engineSize = o.engineSize;
                                r.cylinders = o.cylinders;
                                r.specsCombined = o.specsCombined;
                                r.vin = o.vin;
                                r.antiThft = o.antiThft;
                                r.compSymbol = o.compSymbol;
                                r.collSymbol = o.collSymbol;
                                r.costNewId = o.costNewId;
                                return r;
                            });
                    }
                    return this.vinRecords;
                });
        } else {
            return (Promise.resolve(this.vinRecords));
        }
    }

    vinStepHandler(vin) {
        this.setState({ ...this.state, loadingOptions: true, menuIsOpen: false });

        this.getVinRecords(-1, "", "", vin)
            .then(() => {
                if (0 < this.vinRecords.length)
                {
                    const yearsGroups = _.groupBy(this.vinRecords, "year");
                    const years = _.map(yearsGroups, (records, year) => {
                        return {
                            year: year,
                            count: records.length
                        }
                    });

                    if (1 === years.length) {
                        const yearOption = {
                            value: years[0].year,
                            label: years[0].year,
                            type: VEHICLE_OPTION_TYPES.YEAR
                        };

                        const extendedSelectedOptions = [...this.state.selectedOptions, yearOption];
                        this.setState({ selectedOptions: extendedSelectedOptions, vehicle: { ...this.state.vehicle, year: parseInt(years[0].year, 10) } });
                    } else if (1 < years.length) {
                        this.handleNoVehicleOptions();
                    }


                    const makesGroups = _.groupBy(this.vinRecords, "make");
                    const makes = _.map(makesGroups, (records, make) => {
                        return {
                            make: make,
                            count: records.length
                        }
                    });

                    if (1 === makes.length) {
                        const makeOption = {
                            value: makes[0].make,
                            label: makes[0].make,
                            type: VEHICLE_OPTION_TYPES.MAKE
                        };

                        const extendedSelectedOptions = [...this.state.selectedOptions, makeOption];
                        this.setState({ selectedOptions: extendedSelectedOptions, vehicle: { ...this.state.vehicle, make: makes[0].make} });
                    } else if (1 < makes.length) {
                        this.handleNoVehicleOptions();
                    }


                    const modelsGroups = _.groupBy(this.vinRecords, "model");
                    const models = _.map(modelsGroups, (records, model) => {
                        return {
                            model: model,
                            count: records.length
                        }
                    });

                    if (1 === models.length) {
                        const modelOption = {
                            value: models[0].model,
                            label: models[0].model,
                            type: VEHICLE_OPTION_TYPES.MODEL
                        };

                        const extendedSelectedOptions = [...this.state.selectedOptions, modelOption];

                        this.setState({ selectedOptions: extendedSelectedOptions, loadingOptions: false, menuIsOpen: true }, () => {
                            if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.MODEL, modelOption.value)) {
                                this.handleError();
                            }
                        });
                    } else if (1 < models.length) {
                        const modelOptions = [
                            {
                                label: "Select model",
                                options: models.map(obj => ({
                                    value: obj.model,
                                    label: obj.model,
                                    type: VEHICLE_OPTION_TYPES.MODEL
                                }))
                            }
                        ];

                        this.setState({ ...this.state, options: modelOptions, vehicle: { ...this.state.vehicle, model: models[0].model }, loadingOptions: false, menuIsOpen: true });
                    }

                }
                else {
                    this.handleNoVehicleOptions();
                }
            });
    }

    yearStepHandler() {
        const options: VehicleOption[] = [];
        for (let year = new Date().getFullYear(); year >= START_YEAR; year--) {
            options.push({
                value: year.toString(),
                label: year.toString(),
                type: VEHICLE_OPTION_TYPES.YEAR
            });
        }

        const yearOptions = [{ label: "Select car year", options: options }];
        this.setState({ ...this.state, options: yearOptions, menuIsOpen: true });
    }

    makeStepHandler(prevStepSelectedValue)
    {
        //console.log("year select");
        const vehicleYear = parseInt(prevStepSelectedValue, 10);
        const updatedVehicle = { ...this.state.vehicle, year: vehicleYear };
        this.setState({ ...this.state, vehicle: updatedVehicle, loadingOptions: true, menuIsOpen: false });

        this.apiClient
            .getVehicleMakes(vehicleYear, this.props.quoteId, this.props.quoteUid)
            .then((response) => {
                var makeOptions = [
                    {
                        label: "Select make",
                        options: response.data.options.map(obj => ({
                            ...obj, type: VEHICLE_OPTION_TYPES.MAKE, icon: APP_CONFIG.MAKE_ICONS_PATH + "/" + this.formatMakeIconName(obj.label) + ".png" }))
                    }
                ];

                this.setState({ ...this.state, options: makeOptions, loadingOptions: false, menuIsOpen: true });
            });
    }

    modelStepHandler(prevStepSelectedValue) {
        const updatedVehicle = { ...this.state.vehicle, make: prevStepSelectedValue };
        this.setState({ ...this.state, vehicle: updatedVehicle, loadingOptions: true, menuIsOpen: false });

        this.apiClient
            .getVehicleModels(this.state.vehicle.year, prevStepSelectedValue, this.props.quoteUid)
            .then((response) => {
                var modelOptions = [
                    {
                        label: "Select model",
                        options: response.data.options.map(obj => ({ ...obj, type: VEHICLE_OPTION_TYPES.MODEL }))
                    }
                ];

                this.setState({ ...this.state, options: modelOptions, loadingOptions: false, menuIsOpen: true });
            });
    }

    bodyStepHandler(prevStepSelectedValue) {
        const updatedVehicle = { ...this.state.vehicle, model: prevStepSelectedValue };
        this.setState({ ...this.state, vehicle: updatedVehicle, loadingOptions: true, menuIsOpen: false });

        this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, prevStepSelectedValue, this.state.vehicle.vin)
            .then(() => {
                if (0 < this.vinRecords.length) {
                    const bodyGroups = _.groupBy(this.vinRecords, "body");
                    const bodyTypes = _.map(bodyGroups, (records, bodyType) => {
                        return {
                            body: bodyType,
                            count: records.length
                        }
                    });

                    if (1 === bodyTypes.length) {
                        const bodyTypeOption = { value: bodyTypes[0].body, label: bodyTypes[0].body, type: VEHICLE_OPTION_TYPES.BODY };
                        const extendedSelectedOptions = [...this.state.selectedOptions, bodyTypeOption];
                        this.setState({ selectedOptions: extendedSelectedOptions, loadingOptions: false, menuIsOpen: true }, () => {
                            if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.BODY, bodyTypeOption.value)) {
                                this.handleError();
                            }
                        });
                    } else if (1 < bodyTypes.length) {
                        const bodyTypeOptions = [
                            {
                                label: "Select body type",
                                options: bodyTypes.map(t => ({ value: t.body, label: t.body, type: VEHICLE_OPTION_TYPES.BODY }))
                            }
                        ];

                        this.setState({ ...this.state, options: bodyTypeOptions, loadingOptions: false, menuIsOpen: true });
                    }
                }
                else {
                    this.handleNoVehicleOptions();
                }
            });
    }
    
    engineStepHandler(prevStepSelectedValue) {
        const updatedVehicle = { ...this.state.vehicle, body: prevStepSelectedValue };

        this.setState({ vehicle: updatedVehicle }, () => {
            this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, this.state.vehicle.model, this.state.vehicle.vin)
                .then(() => {
                    if (0 < this.vinRecords.length) {
                        const records = _.filter(this.vinRecords, (record) => record.body === prevStepSelectedValue);
                        const engineGroups = _.groupBy(records, "engineSize");
                        const engineSizes = _.map(engineGroups, (coll, engineSize) => {
                            return {
                                value: engineSize,
                                label: engineSize,
                                type: VEHICLE_OPTION_TYPES.ENGINE
                            }
                        });

                        if (1 === engineSizes.length) {
                            const engineOption = engineSizes[0];
                            const extendedSelectedOptions = [...this.state.selectedOptions, engineOption];
                            this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                                if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.ENGINE, engineOption.value)) {
                                    this.handleError();
                                }
                            });
                        }
                        else if (1 < engineSizes.length) {
                            const engineOptions = [
                                {
                                    label: "Select engine size",
                                    options: engineSizes
                                }
                            ];

                            this.setState({ ...this.state, options: engineOptions });
                        } else {
                            this.handleNoVehicleOptions();
                        }
                    }
                    else {
                        this.handleNoVehicleOptions();
                    }
                });
        });
    }

    cylindersStepHandler(prevStepSelectedValue) {
        const updatedVehicle = { ...this.state.vehicle, engineSize: prevStepSelectedValue };
        this.setState({ vehicle: updatedVehicle }, () => {

            this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, this.state.vehicle.model, this.state.vehicle.vin)
                .then(() => {
                    if (0 < this.vinRecords.length) {
                        const records = _.filter(this.vinRecords, (record) => record.body === this.state.vehicle.body
                            && record.engineSize === prevStepSelectedValue);

                        const cylinderGroups = _.groupBy(records, "cylinders");
                        const cylinders = _.map(cylinderGroups, (coll, cylinderCount) => {
                            return {
                                value: cylinderCount.toString(),
                                label: cylinderCount.toString(),
                                type: VEHICLE_OPTION_TYPES.CYLINDERS
                            }
                        });

                        if (1 === cylinders.length) {
                            const cylinderOption = cylinders[0];
                            const extendedSelectedOptions = [...this.state.selectedOptions, cylinderOption];
                            this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                                if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.CYLINDERS, cylinderOption.value)) {
                                    this.handleError();
                                }
                            });
                        }
                        else if (1 < records.length) {

                            const cylinderOptions = [
                                {
                                    label: "Select cylinders",
                                    options: cylinders
                                }
                            ];

                            this.setState({ ...this.state, options: cylinderOptions });
                        }
                        else {
                            this.handleNoVehicleOptions();
                        }
                    }
                    else {
                        this.handleNoVehicleOptions();
                    }
                });
        });
    }

    specsStepHandler(prevStepSelectedValue) {
        const updatedVehicle = { ...this.state.vehicle, cylinders: parseInt(prevStepSelectedValue, 10) };
        this.setState({ vehicle: updatedVehicle }, () => {
            this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, this.state.vehicle.model, this.state.vehicle.vin)
                .then(() => {
                    if (0 < this.vinRecords.length) {
                        const records = _.filter(this.vinRecords, (record) => record.body === this.state.vehicle.body
                            && record.engineSize === this.state.vehicle.engineSize
                            && record.cylinders === parseInt(prevStepSelectedValue, 10)
                            && record.specsCombined.length > 0);

                        if (0 === records.length) {
                            if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.SPECS, "")) {
                                this.handleError();
                            }

                            return;
                        }

                        const specsGroups = _.groupBy(records, "specsCombined");
                        const specs = _.map(specsGroups, (coll, spec) => {
                            return {
                                value: spec,
                                label: spec,
                                type: VEHICLE_OPTION_TYPES.SPECS
                            }
                        });

                        if (0 < specs.length) {
                            const specOption = specs[0];
                            if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.SPECS, specOption.value)) {
                                this.handleError();
                            }
                            //const extendedSelectedOptions = specOption.value ? [...this.state.selectedOptions, specOption] : this.state.selectedOptions;
                            //this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                            //    if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.SPECS, specOption.value)) {
                            //        this.handleError();
                            //    }
                            //});
                        }
                        else {
                            this.handleNoVehicleOptions();
                        }
                    }

                    //    if (1 === specs.length) {
                    //        const specOption = specs[0];
                    //        const extendedSelectedOptions = specOption.value ? [...this.state.selectedOptions, specOption] : this.state.selectedOptions;
                    //        this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                    //            if (!this.vehicleExplorer.handleNextStepFor(VEHICLE_OPTION_TYPES.SPECS, specOption.value)) {
                    //                this.handleError();
                    //            }
                    //        });
                    //    }
                    //    else if (1 < records.length) {
                    //        const specsOptions = [
                    //            {
                    //                label: "Select specification",
                    //                options: specs.splice(0, 1)
                    //            }
                    //        ];

                    //        this.setState({ ...this.state, options: specsOptions });
                    //    }
                    //    else {
                    //        this.handleNoVehicleOptions();
                    //    }
                    //}
                    else {
                        this.handleNoVehicleOptions();
                    }
                });
        });
    }

    finishStepHandler(prevStepSelectedValue) {
        const updatedVehicle = { ...this.state.vehicle, specs: prevStepSelectedValue };
        this.setState({ vehicle: updatedVehicle, menuIsOpen: false }, () => {
            this.getVinRecords(this.state.vehicle.year, this.state.vehicle.make, this.state.vehicle.model, this.state.vehicle.vin)
                .then(() => {
                    if (0 < this.vinRecords.length) {
                        const records = _.filter(this.vinRecords, (record) => record.body === this.state.vehicle.body
                            && record.engineSize === this.state.vehicle.engineSize
                            && record.cylinders === this.state.vehicle.cylinders
                            && record.specsCombined === prevStepSelectedValue);

                        //console.log("vinRecords");
                        //console.log(this.vinRecords);
                        //console.log("prevStepSelectedValue = " + prevStepSelectedValue);
                        //console.log("state.vehicle");
                        //console.log(this.state.vehicle);

                        if (0 === records.length) {
                            this.handleNoVehicleOptions();
                        } else {
                            this.setState({
                                vehicle: { ...this.state.vehicle, vin: this.state.vehicle.vin.length == 17 ? this.state.vehicle.vin : records[0].vin, antithft: records[0].antiThft, compSymbol: records[0].compSymbol, collSymbol: records[0].collSymbol, costNewId: records[0].costNewId },
                                    updatingAppState: 0 === this.state.vehicle.id,
                                    saveAndContinue: !!this.state.vehicle.id
                                },
                                () => {
                                    if (!this.props.quoteUid) {
                                        this.props.createQuote(this.props.zipCode, this.state.vehicle)
                                            .then(() => this.goNextStep());
                                    }
                                    else if (!this.state.vehicle.id || 0 === this.state.vehicle.id) {
                                        this.props.updateAppState({ quoteUid: this.props.quoteUid, vehicle: this.state.vehicle })
                                            .then(() => this.goNextStep());
                                    }
                                });
                        }
                    }
                    else {
                        this.handleNoVehicleOptions();
                    }
                });
        });
    }

    goNextStep() {
        const nextStepUrl = Utilities.formatUrl(this.props.nextPageUrl ?? this.props.PATHS.VEHICLE_DETAILS,
            (0 < this.props.vehicleIndex ? this.props.vehicleIndex + "" : "")
        );
        //const nextStepUrl = Utilities.formatUrl(PATHS.CONTACT_INFO, "");

        this.props.history.push(nextStepUrl);
    }

    saveAndContinue() {
        this.setState({ updatingAppState: true },
            () => {
                this.props.updateAppState({ quoteUid: this.props.quoteUid, vehicle: this.state.vehicle })
                    .then(() => this.goNextStep());
            });
    }

    handleNoVehicleOptions() {
        this.setState({
            ...this.state,
            options: [{
                    label: "Please try another vehicle",
                    options: [{ label: "No options", value: NO_OPTIONS, type: "" }]
                }],
            loadingOptions: false,
            menuIsOpen: true
        });
    }

    handleError() {
        alert("ERROR !!!");
    }

    handleChange = (newValues: ValueType<any>, action: ActionMeta<VehicleOption>) => {

        switch (action.action) {
            case "select-option":
                const lastSelectedOption = newValues[newValues.length - 1];
                if (NO_OPTIONS === lastSelectedOption.value) {
                    this.vinRecords = [];
                    const updatedVehicle = this.state.vehicle;
                    updatedVehicle.vin = "";
                    this.setState({ options: new Array<OptionsGroup>(), selectedOptions: new Array<VehicleOption>(), vehicle: updatedVehicle },
                        () => this.vehicleExplorer.findStepByType(VEHICLE_OPTION_TYPES.YEAR)?.handler());
                    return;
                };
                const extendedSelectedOptions = [...this.state.selectedOptions, lastSelectedOption];
                this.setState({ selectedOptions: extendedSelectedOptions }, () => {
                    if (!this.vehicleExplorer.handleNextStepFor(lastSelectedOption.type, lastSelectedOption.value)) {
                        this.handleError();
                    }
                });
                break;
            case "remove-value":
                if (undefined === action.removedValue) return;
                this.vinRecords = [];
                const removedValueType = action.removedValue.type;
                const reducedSelectedOptions = [...this.state.selectedOptions];
                const updatedVehicle = this.state.vehicle;
                for (let i = 0; i < reducedSelectedOptions.length; i++) {
                    if (reducedSelectedOptions[i].type === removedValueType) {
                        reducedSelectedOptions.splice(i, reducedSelectedOptions.length);
                        updatedVehicle.vin = "";
                        break;
                    }
                }

                this.setState({ selectedOptions: reducedSelectedOptions, saveAndContinue: false, vehicle: updatedVehicle }, () => {
                    const newVehicleStep = this.vehicleExplorer.findStepByType(removedValueType);
                    const lastSelectedValue = 0 < reducedSelectedOptions.length
                        ? reducedSelectedOptions[reducedSelectedOptions.length - 1].value
                        : "";
                    if (newVehicleStep) {
                        newVehicleStep.handler(lastSelectedValue);
                    } else {
                        this.handleError();
                    }
                });
                this.selectRef.current?.focus();
                break;
        }
    };

    formatMakeIconName(make: string) {
        return make.replace(/\s/g, "_");
    }

    onVinChange(vin: string)
    {
        this.vinRecords = [];

        if (vin.length == 17) {
            this.setState({
                ...this.state, vinValid: true, vehicle: { ...this.state.vehicle, vin: vin, id: this.state.vehicle.id },
                options: new Array<OptionsGroup>(), selectedOptions: new Array<VehicleOption>()
            }, () => {
                    if (vin.match(/^[0-9A-HJ-NPR-Z\\&]{17}$/i)) {
                    this.vinStepHandler(vin);
                }
                else {
                    this.setState({
                        ...this.state, vinValid: false, vehicle: { ...this.state.vehicle, vin: vin, id: this.state.vehicle.id },
                        options: new Array<OptionsGroup>(), selectedOptions: new Array<VehicleOption>()
                    });
                }
            });
        }
        else {
            this.setState({
                ...this.state, vinValid: true, vehicle: { ...this.state.vehicle, vin: vin, id: this.state.vehicle.id },
                options: new Array<OptionsGroup>(), selectedOptions: new Array<VehicleOption>()
            }, () => this.yearStepHandler());
        }
    }

    render() {
        return (
            <QuoteStep id={this.props.id} header={this.props.header}>
                <div className={"vehicle-ymm"}>
                    <h5 className="card-title text-center">Let's get started with your car information</h5>

                    <div className="form-group mx-sm-3 mb-2">
                        <ValidatableFormGroup
                            label=""
                            style={{ marginBottom: "0px" }}
                            maxLength={17}
                            value={this.state.vehicle.vin}
                            placeholder="Enter Your VIN"
                            validated={!this.state.vinValid}
                            validationError={"Incorrect VIN number"}
                            onChange={(e) => { this.onVinChange(e.target.value)}}/>

                        <div className="orText"><p>or</p></div>

                        <Select
                            isMulti
                            ref={this.selectRef}
                            options={this.state.options}
                            value={this.state.selectedOptions}
                            placeholder={"Find Your Car"}
                            menuIsOpen={this.state.menuIsOpen}
                            openMenuOnFocus={true}
                            onChange={this.handleChange}
                            isSearchable={false}
                            blurInputOnSelect={false}
                            autoFocus={true}
                            components={{ Option }}
                            isClearable={false}
                        />
                    </div>

                    <div className={"w-100 loading-indicator-center" + (this.state.loadingOptions || (!this.state.saveAndContinue && this.state.updatingAppState) ? " visible" : " invisible")}>
                        <ReactLoading type={"spin"} color={"#007bff"} height={'40px'} width={'40px'} />
                    </div>

                    <div className="btnContainer" style={{ display: this.state.saveAndContinue ? '' : 'none' }}>
                        <Button
                            className="btnContinue"
                            size="sm"
                            disabled={this.state.updatingAppState }
                            onClick={this.saveAndContinue}>Save <i className="fas fa-arrow-right"></i></Button>
                    </div>
                </div>
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState, ownProps: VehicleYMMProps) => {
    var vehicleIndex = !ownProps.match.params["vehicleIndex"]
        ? 0
        : parseInt(ownProps.match.params["vehicleIndex"], 10);

    return {
        zipCode : state.zipCode,
        quoteUid: state.quoteUid,
        quoteId: state.quoteId,
        vehicleIndex: vehicleIndex,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE },
        vehicle: vehicleIndex < state.vehicles.length ? state.vehicles[vehicleIndex] : null,
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: VehicleYMMProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(QUOTE_STEPS.VEHICLE_YMM)),
        createQuote: (zipCode, vehicle) => dispatch(quoteActions.createQuote(zipCode, vehicle)),
        updateAppState: (update) => dispatch(quoteActions.updateVehicle(update.quoteUid, update.vehicle, QUOTE_STEPS.VEHICLE_YMM))
    };
};

export default connect(mapProps, mapDispatch)(VehicleYMM);